import { z } from 'zod'

import { voucherSchema } from './voucherSchemas'

/* Voucher Users */
export const voucherUserWithRoleSchema = z.object({
  id: z.string(),
  user_name: z.string(),
  email: z.string(),
  role: z.string(),
})
export type VoucherUserWithRole = z.infer<typeof voucherUserWithRoleSchema>

export const voucherUsersResponseSchema = z.object({
  users: z.array(voucherUserWithRoleSchema),
})
export type VoucherUsersResponse = z.infer<typeof voucherUsersResponseSchema>

/* List */
export const voucherListResponseSchema = z.object({
  vouchers: z.array(voucherSchema),
})
export type VoucherListResponse = z.infer<typeof voucherListResponseSchema>

/* Get */
export const voucherGetResponseSchema = voucherSchema
export type voucherGetResponseSchema = z.infer<typeof voucherGetResponseSchema>

/* Generic Action Response */
export const voucherActionResponseSchema = z.object({
  result: z.string(),
  message: z.string(),
  voucher: voucherSchema.nullish(),
})
export type VoucherActionResponse = z.infer<typeof voucherActionResponseSchema>

/* Create */
export const voucherCreateRequestSchema = z.object({
  title: z.string().trim().min(1, { message: 'Please enter a title.' }),
  description: z.string().optional(),
  voucher_id: z.string().optional(),
  responsible_officer: z.string().optional(),
  directorate: z.string().optional(),
  office: z.string().optional(),
  vendor_code: z.string().optional(),
  comment: z.string().optional(),
})
export type VoucherCreateRequest = z.infer<typeof voucherCreateRequestSchema>

/* Delete */
export const voucherDeleteRequestSchema = z.object({
  comment: z.string().optional(),
})
export type VoucherDeleteRequest = z.infer<typeof voucherDeleteRequestSchema>

/* Cancel */
export const voucherCancelRequestSchema = z.object({
  comment: z.string().optional(),
})
export type VoucherCancelRequest = z.infer<typeof voucherCancelRequestSchema>

/* Edit Draft */
export const voucherEditDraftRequestSchema = z.object({
  title: z.string().optional(),
  voucher_id: z.string().optional(),
  responsible_officer: z.string().optional(),
  directorate: z.string().optional(),
  office: z.string().optional(),
  vendor_code: z.string().optional(),
  comment: z.string().optional(),
})
export type VoucherEditDraftRequest = z.infer<typeof voucherEditDraftRequestSchema>

/* Edit Review */
export const voucherEditReviewRequestSchema = z.object({
  directorate: z.string().min(1, { message: 'Please enter a directorate.' }),
  office: z.string().min(1, { message: 'Please enter an office.' }),
  comment: z.string().trim().min(1, { message: 'Please enter a justification.' }),
})
export type VoucherEditReviewRequest = z.infer<typeof voucherEditReviewRequestSchema>

/* Add Comment */
export const voucherAddCommentRequestSchema = z.object({
  comment: z.string().trim().min(1, { message: 'Please enter a comment.' }),
})
export type VoucherAddCommentRequest = z.infer<typeof voucherAddCommentRequestSchema>

/* Add Transactions */
export const voucherAddTransactionsRequestSchema = z.object({
  transaction_ids: z.array(z.string()),
  purpose: z.string(),
  obligation: z.string(),
  officer: z.string(),
  comment: z.string().optional(),
})
export type VoucherAddTransactionsRequest = z.infer<typeof voucherAddTransactionsRequestSchema>

export const voucherAddTransactionsResponseSchema = z.object({
  result: z.array(
    z.object({
      id: z.string(),
      org: z.string(),
      voucher: z.string(),
      transaction_group_id: z.string(),
      transaction_id: z.string(),
      created_at: z.string().datetime(),
      transaction_details: z.record(z.string(), z.any()),
      purpose: z.string(),
      obligation: z.string(),
      certifier_description: z.string().nullable().optional(),
      officer: z.string(),
    })
  ),
})
export type VoucherAddTransactionsResponse = z.infer<typeof voucherAddTransactionsResponseSchema>

/* Edit Single Transaction */
export const voucherEditSingleTransactionRequestSchema = z.object({
  purpose: z.string().optional(),
  obligation: z.string().optional(),
  officer: z.string().optional(),
  comment: z.string().optional(),
})
export type VoucherEditSingleTransactionRequest = z.infer<
  typeof voucherEditSingleTransactionRequestSchema
>

/* Remove Transactions */
export const voucherRemoveTransactionsRequestSchema = z.object({
  transaction_ids: z.array(z.string()),
  comment: z.string().trim().min(1, { message: 'Please enter a justification.' }),
})
export type VoucherRemoveTransactionsRequest = z.infer<
  typeof voucherRemoveTransactionsRequestSchema
>

export const voucherRemoveTransactionsResponseSchema = z.object({
  result: z.string(),
})
export type VoucherRemoveTransactionsResponse = z.infer<
  typeof voucherRemoveTransactionsResponseSchema
>

/* Submit Draft */
export const voucherSubmitDraftRequestSchema = z.object({
  title: z.string().trim().min(1, { message: 'Please enter a title.' }),
  voucher_id: z.string().trim().min(1, { message: 'Please enter a voucher ID.' }),
  description: z.string().trim().min(1, { message: 'Please enter a description.' }),
  responsible_officer: z.string().optional(),
  directorate: z.string().trim().min(1, { message: 'Please enter a directorate.' }),
  office: z.string().trim().min(1, { message: 'Please enter an office.' }),
  vendor_code: z.string().trim().min(1, { message: 'Please enter a vendor code.' }),
  comment: z.string().optional(),
})
export type VoucherSubmitDraftRequest = z.infer<typeof voucherSubmitDraftRequestSchema>

/* Submit Audit */
export const voucherSubmitAuditRequestSchema = z.object({
  comment: z.string().optional(),
})
export type VoucherSubmitAuditRequest = z.infer<typeof voucherSubmitAuditRequestSchema>

/* Submit Approval */
export const voucherSubmitApprovalRequestSchema = z.object({
  comment: z.string().optional(),
})
export type VoucherSubmitApprovalRequest = z.infer<typeof voucherSubmitApprovalRequestSchema>

/* Submit Certification */
export const voucherSubmitCertificationRequestSchema = z.object({
  comment: z.string().optional(),
})
export type VoucherSubmitCertificationRequest = z.infer<
  typeof voucherSubmitCertificationRequestSchema
>

/* Rework */
export const voucherReworkRequestSchema = z.object({
  comment: z.string().trim().min(1, { message: 'Please enter a justification.' }),
})
export type VoucherReworkRequest = z.infer<typeof voucherReworkRequestSchema>

/* Sync */
export const voucherSyncRequestSchema = z.object({
  comment: z.string().optional(),
})
export type VoucherSyncRequest = z.infer<typeof voucherSyncRequestSchema>

/* Export */
export const voucherExportRequestSchema = z.object({
  comment: z.string().optional(),
})
export type VoucherExportRequest = z.infer<typeof voucherExportRequestSchema>

export const pdfResultSchema = z.object({
  artifact_id: z.string(),
  s3_key: z.string(),
  file_name: z.string(),
  url: z.string(),
  expires_in_seconds: z.number(),
})
export type PdfResult = z.infer<typeof pdfResultSchema>

export const voucherExportResponseSchema = voucherActionResponseSchema.extend({
  pdf: pdfResultSchema,
})
export type VoucherExportResponse = z.infer<typeof voucherExportResponseSchema>
